import React, { FunctionComponent, useEffect, useRef, useState } from "react";
import { Box, DialogContent, Stack, Toolbar } from "@mui/material";
import styles from "./styles.module.scss";
import { StatusCodesHelper } from "../../../../models/StatusCodesHelper";
import { useGetImage } from "../../../../hooks/images";
import { ImageCache } from "../../../../models/ImageCache";
import { strings } from "../../../../localization/LocalizedStrings";
import { ToolbarButton } from "../../../toolbar/ToolbarButton";
import { useIsMobile } from "../../../../hooks/isMobile";
import { LazyImageAspectFit } from "../../../LazyImageAspectFit";
import { PiperDialog } from "../../../PiperDialog";
import { IconType } from "../../../../icons";
import { DialogState } from "../../../../models/DialogState";
import { IGearItem } from "../../../../hooks/gearItems";
import { ModifyGearItemDialog } from "../ModifyGearItemDialog";

export const GearItemDialog: FunctionComponent<IGearItemDialogProps> = ({
  gearItem,
  isOpen,
  onClose,
  canEdit,
  onModifiedItem,
  onDeletedItem
}) => {
  const isMobile = useIsMobile();
  const [myGearItem, setMyGearItem] = useState(gearItem);
  const { getImage } = useGetImage();
  const [image, setImage] = useState();
  const [thumbnail, setThumbnail] = useState(gearItem.thumbnail);
  const [modifyDialogState, setModifyDialogState] = useState<DialogState<IGearItem>>({
    mode: "closed"
  });
  const genericImage = new URL("../../../../images/generic_label.png", import.meta.url).href;
  useEffect(() => {
    setThumbnail(myGearItem.thumbnail);
    fetchImage().then();
  }, [myGearItem.uuid, myGearItem.hasImage, myGearItem.thumbnail]);
  useEffect(() => {
    setMyGearItem(gearItem);
  }, [gearItem]);
  const currentGearItemUuid = useRef("");
  const fetchImage = async () => {
    currentGearItemUuid.current = myGearItem.uuid;
    setImage(undefined);
    if (!myGearItem.hasImage) return;
    const response = await getImage({
      name: myGearItem.uuid,
      width: ImageCache.bigLabelImageSize,
      height: ImageCache.bigLabelImageSize
    });
    // Discard image if label changed
    if (currentGearItemUuid.current != myGearItem.uuid) return;
    if (StatusCodesHelper.isSuccessful(response.status)) {
      setImage(response.body);
    }
  };
  return (
    <PiperDialog fullWidth maxWidth={"sm"} open={isOpen} onClose={onClose}>
      <div className={styles.dialog}>
        <Toolbar>
          <h3>{myGearItem.name}</h3>
          <Box flexGrow={1} />
          {canEdit && (
            <ToolbarButton
              onClick={() => setModifyDialogState({ mode: "edit", item: gearItem })}
              tooltip={strings.gear_item_edit}
              iconType={IconType.EditSquare}
            />
          )}
          <ToolbarButton onClick={onClose} tooltip={strings.close} iconType={IconType.Close} />
        </Toolbar>
      </div>
      <DialogContent dividers>
        {myGearItem ? (
          <Stack spacing={2}>
            <Stack direction={isMobile ? "column" : "row"} spacing={2}>
              <LazyImageAspectFit
                className={styles.image}
                style={{ width: isMobile ? "100%" : "50%" }}
                imageStyle={{ borderRadius: "6%" }}
                image={image}
                thumbnail={thumbnail}
                defaultImage={genericImage}
                alt={myGearItem.name}
              />
            </Stack>
            <p className={styles.profile}>{myGearItem.description}</p>
          </Stack>
        ) : (
          <Box sx={{ height: 200 }} />
        )}
      </DialogContent>
      <ModifyGearItemDialog
        key={modifyDialogState.item?.uuid}
        open={modifyDialogState.mode !== "closed"}
        onClose={() => setModifyDialogState({ mode: "closed", item: modifyDialogState.item })}
        gearItem={modifyDialogState.item}
        originalImageData={image}
        onModifiedItem={newItem => {
          onModifiedItem?.(newItem);
          setMyGearItem(newItem);
        }}
        onDeletedItem={onDeletedItem}
      />
    </PiperDialog>
  );
};

export interface IGearItemDialogProps {
  gearItem: IGearItem;
  isOpen: boolean;
  onClose: () => void;
  canEdit: boolean;
  onModifiedItem?: (gearItem: IGearItem) => void;
  onDeletedItem?: (gearItem: IGearItem) => void;
}
